import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { FacebookShareButton } from 'react-share';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router';
import ReactTooltip from 'react-tooltip';

import Button from '../../../../../../../components/global/Button';
import { useContributorPermission } from '../../../../../services/contributors';
import DownloadPhotoButtonContainer from '../../../../../../../containers/global/DownloadPhotoButtonContainer';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import { SHARE_BLOCK_MODAL } from '../../../../../../../constants/modals';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import getConfig from '../../../../../../../util/config';
import SearchIcon from '../../../../../../../components/icons/SearchIcon';
import MatchedPhotoFavoriteButton from '../MatchedPhotoFavoriteButton/MatchedPhotoFavoriteButton';
import DotTrigger from '../../../../../../../components/global/DotTrigger';
import FavoritesIcon from '../../../../../../../components/icons/FavoritesIcon';
import FavoritesEmptyIcon from '../../../../../../../components/icons/FavoritesEmptyIcon';
import { WEB_GALLERY_DOWNLOAD_MODAL } from '../../../../../../web-gallery/components/WebGalleryDownloadModal/WebGalleryDownloadModal';
import AddPrintPackageIcon from '../../../../../../../components/icons/AddPrintPackageIcon';
import ShoppingBasketIcon from '../../../../../../../components/icons/ShoppingBasketIcon';
import CheckmarkIcon from '../../../../../../../components/icons/CheckmarkIcon';
import { breakpointDown } from '../../../../../../../services/responsive/responsive';
import Angle from '../../../../../../../components/global/Angle';
import { ALBUM_SUBTYPE } from '../../../../../../../constants/albums';

const propTypes = {
  matchedPhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
        }),
      ),
      share_blocked: PropTypes.bool,
      is_favorite: PropTypes.bool,
    }).isRequired,
    album: PropTypes.shape({
      id: PropTypes.string,
      print_without_watermark: PropTypes.bool,
    }),
  }).isRequired,
  hasUnconfiguredPrePurchases: PropTypes.bool,
  canPurchasePhotoItems: PropTypes.bool.isRequired,
  canReportPhoto: PropTypes.bool,
  canDownloadPhoto: PropTypes.bool,
  canConfigureGreenScreenPhoto: PropTypes.bool,
  isEntitled: PropTypes.bool.isRequired,
  onConfigurePrepaidItems: PropTypes.func,
  onPhotoItemPurchase: PropTypes.func.isRequired,
  hasCartItems: PropTypes.bool,
  onPhotoReport: PropTypes.func.isRequired,
  socialShareEnabled: PropTypes.bool,
  hashCode: PropTypes.string,
  openModal: PropTypes.func.isRequired,
  isAllPhotos: PropTypes.bool,
  linkTo: PropTypes.string,
  withHighlights: PropTypes.bool,
  onDeleteHighlight: PropTypes.func,
  onPhotoItemWaldogramPurchase: PropTypes.func,
  onPhotoItemCampergramPurchase: PropTypes.func,
  sellPhotosModeEnabled: PropTypes.bool,
  hierarchyItemId: PropTypes.string,
};

const MatchedPhotoActions = ({
  matchedPhoto,
  isEntitled,
  hasUnconfiguredPrePurchases,
  canPurchasePhotoItems,
  canReportPhoto,
  canDownloadPhoto = true,
  canConfigureGreenScreenPhoto,
  onConfigurePrepaidItems,
  onPhotoItemPurchase,
  hasCartItems,
  onPhotoReport,
  socialShareEnabled,
  hashCode,
  openModal,
  isAllPhotos,
  linkTo,
  withHighlights,
  onDeleteHighlight,
  onPhotoItemWaldogramPurchase,
  onPhotoItemCampergramPurchase,
  sellPhotosModeEnabled,
  hierarchyItemId,
}) => {
  const history = useHistory();
  const [waldogramsToggled, setWaldogramsToggled] = useState(false);
  const { photo, album } = matchedPhoto;
  const renderFacebookShare = <Fragment>Share on Facebook</Fragment>;
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const renderCampergramOption =
    !!onPhotoItemCampergramPurchase ||
    album?.album_subtype === ALBUM_SUBTYPE.CAMPS;
  const renderDropdownMenu =
    isAllPhotos ||
    isEntitled ||
    canReportPhoto ||
    (isMobile &&
      (isAllPhotos ||
        isEntitled ||
        (canPurchasePhotoItems && !sellPhotosModeEnabled)));
  const { isContributor } = useContributorPermission({
    entityId: album?.id,
  });

  const [showDownloads, setShowDownloads] = useState(false);

  return (
    <Fragment>
      <div className="base-overlay" />
      {sellPhotosModeEnabled && (
        <div className="matched-grid-photo-purchase-buttons">
          {hasUnconfiguredPrePurchases && (
            <Button
              onClick={onConfigurePrepaidItems}
              className="matched-grid-photo-action-btn matched-grid-photo-btn-purchase"
              data-testid="matched-grid-photo-purchase-unconfigured-print-package"
            >
              <AddPrintPackageIcon color="#fff" size={14} className="mr-1" />
              Add to order
            </Button>
          )}
          {!hasUnconfiguredPrePurchases &&
            canPurchasePhotoItems &&
            !isEntitled && (
              <Button
                onClick={onPhotoItemPurchase}
                className="matched-grid-photo-action-btn matched-grid-photo-btn-purchase ml-1"
                data-testid="matched-grid-photo-purchase-photo-items"
              >
                {hasCartItems ? (
                  <div className="checked-cart">
                    <ShoppingBasketIcon
                      color="#fff"
                      size={14}
                      className="mr-1"
                    />
                    <div>
                      <CheckmarkIcon size={7} className="cart-checkmark" />
                    </div>
                  </div>
                ) : (
                  <ShoppingBasketIcon color="#fff" size={14} className="mr-1" />
                )}

                {hasCartItems ? 'In cart' : <Fragment>Buy</Fragment>}
              </Button>
            )}
        </div>
      )}
      <div className="matched-grid-photo-actions">
        {!(isEntitled || isAllPhotos) && linkTo && (
          <Button
            to={linkTo}
            className="matched-grid-photo-action-btn matched-grid-photo-btn-zoom"
          >
            <SearchIcon color="#fff" />
          </Button>
        )}

        {(isAllPhotos || isEntitled) && !isMobile && (
          <MatchedPhotoFavoriteButton
            photo={photo}
            album={album}
            className="matched-grid-photo-action-btn mr-auto"
            icon={
              photo.is_favorite ? (
                <FavoritesIcon color="#f8dc3e" />
              ) : (
                <FavoritesEmptyIcon color="white" />
              )
            }
          />
        )}

        {renderDropdownMenu && (
          <div className="dropdown-menu-dark">
            <DropdownMenu
              toggle={<DotTrigger dotType="vertical" />}
              toggleClassName="matched-grid-photo-action-btn matched-grid-photo-btn-more-actions"
              dropdownClassName="matched-grid-photo-btn-more-actions-dropdown"
              placement="bottom-end"
              aria-label="Photo Actions"
              closeOnMouseLeave
            >
              {({ onDropdownToggle }) => (
                <Fragment>
                  {(isAllPhotos || isEntitled) && isMobile && (
                    <MatchedPhotoFavoriteButton photo={photo} album={album}>
                      {({ onFavoriteToggle }) => (
                        <Button
                          btnStyle="reset"
                          className="dropdown-item"
                          onClick={() => {
                            onFavoriteToggle?.();
                            onDropdownToggle?.();
                          }}
                        >
                          {photo.is_favorite ? (
                            <>Remove from Favorites</>
                          ) : (
                            <>Add to Favorites</>
                          )}
                        </Button>
                      )}
                    </MatchedPhotoFavoriteButton>
                  )}
                  {canDownloadPhoto && (
                    <Fragment>
                      {isAllPhotos ? (
                        <Button
                          className="dropdown-item"
                          btnStyle="reset"
                          onClick={() =>
                            openModal(WEB_GALLERY_DOWNLOAD_MODAL, {
                              photo,
                              album,
                              forceAllowHiResDownloads: isEntitled,
                            })
                          }
                        >
                          Download
                        </Button>
                      ) : (
                        (() => {
                          if (isContributor) {
                            return (
                              <Fragment>
                                <div className="downloads-container">
                                  <Button
                                    btnStyle="reset"
                                    className="dropdown-item"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setShowDownloads(prev => !prev);
                                    }}
                                  >
                                    Download{' '}
                                    <Angle
                                      direction={showDownloads ? 'up' : 'down'}
                                      className="ml-1"
                                    />
                                  </Button>
                                  {showDownloads && (
                                    <Fragment>
                                      <DownloadPhotoButtonContainer
                                        photoId={photo?.id}
                                        photoSize={IMAGE_SIZE_CODE.LARGE2X}
                                        includeWatermark={false}
                                        btnStyle="reset"
                                        btnSize="xs"
                                        className="dropdown-item h-auto-i"
                                        onMouseDown={e => e.stopPropagation()}
                                        onDownload={() =>
                                          setShowDownloads(false)
                                        }
                                        data-for={`${photo.id}-action-menu`}
                                        data-tip="Download Hi-res"
                                        data-place="left"
                                      >
                                        <p className="mb-0">
                                          Download Hi-Res
                                          <br />
                                          <span className="text-light text-xs">
                                            Great for prints up to 8x10
                                          </span>
                                        </p>
                                      </DownloadPhotoButtonContainer>
                                      <DownloadPhotoButtonContainer
                                        photoId={photo?.id}
                                        photoSize={IMAGE_SIZE_CODE.LARGE2X}
                                        includeWatermark={false}
                                        downloadOriginal
                                        btnStyle="reset"
                                        btnSize="xs"
                                        className="dropdown-item h-auto-i"
                                        onMouseDown={e => e.stopPropagation()}
                                        onDownload={() =>
                                          setShowDownloads(false)
                                        }
                                        data-for={`${photo.id}-action-menu-2`}
                                        data-tip="Download Original"
                                        data-place="left"
                                      >
                                        <p className="mb-0">
                                          Download Original
                                          <br />
                                          <span className="text-light text-xs">
                                            Great for large format printing
                                          </span>
                                        </p>
                                      </DownloadPhotoButtonContainer>
                                    </Fragment>
                                  )}
                                </div>
                              </Fragment>
                            );
                          }
                          return (
                            <Fragment>
                              {isEntitled && canDownloadPhoto && (
                                <DownloadPhotoButtonContainer
                                  photoId={photo.id}
                                  photoSize={IMAGE_SIZE_CODE.LARGE2X}
                                  className="dropdown-item"
                                  onDownload={() => onDropdownToggle?.()}
                                >
                                  Download Hi-res
                                </DownloadPhotoButtonContainer>
                              )}
                            </Fragment>
                          );
                        })()
                      )}
                    </Fragment>
                  )}
                  {canConfigureGreenScreenPhoto && isEntitled && (
                    <Button
                      btnStyle="reset"
                      className="dropdown-item"
                      onClick={() =>
                        history.push(
                          `/cards/downloads-configurator/${
                            hierarchyItemId ?? album?.id
                          }?photoIds[]=${photo.id}`,
                        )
                      }
                      data-testid="matched-grid-photo-download-configurator"
                    >
                      Configure for download
                    </Button>
                  )}
                  {canPurchasePhotoItems && isEntitled && (
                    <Button
                      btnStyle="reset"
                      className="dropdown-item"
                      onClick={onPhotoItemPurchase}
                      data-testid="matched-grid-photo-purchase-photo-items"
                    >
                      Buy prints &amp; more&hellip;
                    </Button>
                  )}
                  {isEntitled &&
                    onPhotoItemWaldogramPurchase &&
                    !renderCampergramOption && (
                      <Button
                        className="dropdown-item"
                        onClick={onPhotoItemWaldogramPurchase}
                      >
                        Send a card
                      </Button>
                    )}
                  {isEntitled &&
                    !onPhotoItemWaldogramPurchase &&
                    onPhotoItemCampergramPurchase && (
                      <Button
                        className="dropdown-item"
                        onClick={onPhotoItemCampergramPurchase}
                      >
                        Send a card
                      </Button>
                    )}
                  {isEntitled &&
                    onPhotoItemWaldogramPurchase &&
                    renderCampergramOption && (
                      <div className="waldograms-dropdown-container">
                        <Button
                          btnStyle="reset"
                          className="dropdown-item"
                          onClick={() => setWaldogramsToggled(prev => !prev)}
                        >
                          Send a card
                          <Angle
                            direction={waldogramsToggled ? 'up' : 'down'}
                            className="ml-1"
                          />
                        </Button>
                        {waldogramsToggled && (
                          <Fragment>
                            <Button
                              btnStyle="reset"
                              className="dropdown-item ml-1"
                              onClick={onPhotoItemCampergramPurchase}
                              disabled={!onPhotoItemCampergramPurchase}
                            >
                              <p
                                className="mb-0"
                                data-tip=""
                                data-for={`${photo?.id}-campergram-disabled`}
                                data-effect="solid"
                              >
                                Send a card to your camper
                                {!onPhotoItemCampergramPurchase && (
                                  <ReactTooltip
                                    id={`${photo?.id}-campergram-disabled`}
                                    place="bottom"
                                    className="tooltip"
                                  >
                                    Camper postcards are not
                                    <br />
                                    available or no longer available
                                    <br />
                                    due to delivery time.
                                  </ReactTooltip>
                                )}
                              </p>
                            </Button>
                            <Button
                              btnStyle="reset"
                              className="dropdown-item ml-1"
                              onClick={onPhotoItemWaldogramPurchase}
                            >
                              <p className="mb-0">
                                Send a card to someone else
                              </p>
                            </Button>
                          </Fragment>
                        )}
                      </div>
                    )}
                  {!sellPhotosModeEnabled && hasUnconfiguredPrePurchases && (
                    <Button
                      onClick={onConfigurePrepaidItems}
                      className="dropdown-item"
                      data-testid="matched-grid-photo-purchase-unconfigured-print-package"
                    >
                      Add to order
                    </Button>
                  )}
                  {withHighlights && onDeleteHighlight && (
                    <Button
                      className="dropdown-item"
                      btnStyle="reset"
                      onClick={() => {
                        onDeleteHighlight?.();
                        onDropdownToggle?.();
                      }}
                    >
                      Remove from Highlights
                    </Button>
                  )}
                  {((socialShareEnabled && isEntitled) || canReportPhoto) && (
                    <Fragment>
                      {socialShareEnabled &&
                        isEntitled &&
                        (photo.share_blocked ? (
                          <button
                            className="dropdown-item text-faded"
                            onClick={() => openModal(SHARE_BLOCK_MODAL)}
                          >
                            {renderFacebookShare}
                          </button>
                        ) : (
                          <FacebookShareButton
                            className="dropdown-item"
                            url={`${getConfig().app.baseUrl}/p/${photo.id}`}
                            hashtag={hashCode && `#${hashCode}`}
                          >
                            {renderFacebookShare}
                          </FacebookShareButton>
                        ))}

                      {canReportPhoto && (
                        <Button
                          className="dropdown-item text-danger"
                          onClick={() => {
                            onPhotoReport(photo.id);
                            onDropdownToggle?.();
                          }}
                        >
                          Report this photo
                        </Button>
                      )}
                    </Fragment>
                  )}
                </Fragment>
              )}
            </DropdownMenu>
          </div>
        )}
      </div>
    </Fragment>
  );
};

MatchedPhotoActions.propTypes = propTypes;

export default MatchedPhotoActions;
